// frontend/src/admin/pages/AdminBannerPage.jsx
import React, { useCallback, useEffect, useState } from "react";

import {
  listBanners,
  createBanner,
  updateBanner,
  deleteBanner,
} from "../api";

const FONT_REM = { s: "0.8125rem", m: "0.9375rem", l: "1.125rem", xl: "1.375rem" };

const BLANK = {
  message: "",
  link_url: "",
  link_label: "",
  bg_color: "#4f46e5",
  text_color: "#ffffff",
  font_size: "m",
  enabled: false,
  dismissible: true,
  starts_at: "",
  ends_at: "",
};

/** A live render of the public <SiteBanner>, driven by the current form. */
function BannerPreview({ form }) {
  return (
    <div
      className="rounded-lg overflow-hidden border border-slate-200"
      style={{ background: form.bg_color, color: form.text_color }}
    >
      <div className="mx-auto flex max-w-3xl items-center justify-center gap-3 px-4 py-2 text-center">
        <p
          className="font-medium"
          style={{ fontSize: FONT_REM[form.font_size] || FONT_REM.m }}
        >
          {form.message || "Your announcement text…"}
          {form.link_url && (
            <span className="ml-2 underline underline-offset-2">
              {form.link_label || "Learn more"}
            </span>
          )}
        </p>
        {form.dismissible && <span className="ml-1 text-lg leading-none">×</span>}
      </div>
    </div>
  );
}

/** Strip an ISO datetime to the yyyy-MM-dd a <input type=date> expects. */
function toDateInput(iso) {
  return iso ? iso.slice(0, 10) : "";
}

export default function AdminBannerPage() {
  const [banners, setBanners] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listBanners()
      .then(setBanners)
      .catch(() => setError("Failed to load banners."));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => {
    setForm(BLANK);
    setEditingId(null);
  };

  const edit = (b) => {
    setEditingId(b.id);
    setForm({
      message: b.message,
      link_url: b.link_url || "",
      link_label: b.link_label || "",
      bg_color: b.bg_color,
      text_color: b.text_color,
      font_size: b.font_size,
      enabled: b.enabled,
      dismissible: b.dismissible,
      starts_at: toDateInput(b.starts_at),
      ends_at: toDateInput(b.ends_at),
    });
  };

  const save = async () => {
    setBusy(true);
    setError("");
    const body = {
      ...form,
      link_url: form.link_url || null,
      link_label: form.link_label || null,
      starts_at: form.starts_at
        ? new Date(form.starts_at).toISOString()
        : null,
      ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
    };
    try {
      if (editingId) await updateBanner(editingId, body);
      else await createBanner(body);
      reset();
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save banner.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteBanner(id);
      if (editingId === id) reset();
      load();
    } catch {
      setError("Failed to delete banner.");
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <header>
        <h1 className="text-xl font-bold text-slate-900">Announcements</h1>
        <p className="text-sm text-slate-500 mt-1">
          A styleable band shown at the top of the public site. Only the single
          active banner (enabled and within its date window) is shown to
          visitors.
        </p>
      </header>

      {error && (
        <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* Live preview */}
      <div>
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Preview
        </h2>
        <BannerPreview form={form} />
      </div>

      {/* Editor */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h2 className="text-base font-bold text-slate-900">
          {editingId ? "Edit banner" : "New banner"}
        </h2>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            Message
          </label>
          <input
            type="text"
            aria-label="Message"
            value={form.message}
            maxLength={300}
            onChange={(e) => set("message", e.target.value)}
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Link URL (optional)
            </label>
            <input
              type="text"
              value={form.link_url}
              onChange={(e) => set("link_url", e.target.value)}
              className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Link label
            </label>
            <input
              type="text"
              value={form.link_label}
              onChange={(e) => set("link_label", e.target.value)}
              className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Background
            </label>
            <input
              type="color"
              aria-label="Background colour"
              value={form.bg_color}
              onChange={(e) => set("bg_color", e.target.value)}
              className="h-9 w-14 rounded border border-slate-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Text
            </label>
            <input
              type="color"
              aria-label="Text colour"
              value={form.text_color}
              onChange={(e) => set("text_color", e.target.value)}
              className="h-9 w-14 rounded border border-slate-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Font size
            </label>
            <select
              aria-label="Font size"
              value={form.font_size}
              onChange={(e) => set("font_size", e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            >
              <option value="s">Small</option>
              <option value="m">Medium</option>
              <option value="l">Large</option>
              <option value="xl">X-Large</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Start date (optional)
            </label>
            <input
              type="date"
              value={form.starts_at}
              onChange={(e) => set("starts_at", e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              End date (optional)
            </label>
            <input
              type="date"
              value={form.ends_at}
              onChange={(e) => set("ends_at", e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            />
          </div>
        </div>

        <div className="flex items-center gap-5 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => set("enabled", e.target.checked)}
            />
            Enabled
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.dismissible}
              onChange={(e) => set("dismissible", e.target.checked)}
            />
            Dismissible
          </label>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy || !form.message}
            onClick={save}
            className="rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40"
          >
            {editingId ? "Save changes" : "Create banner"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={reset}
              className="rounded-md border border-slate-300 px-4 py-1.5 text-sm font-semibold text-slate-600"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Existing banners */}
      <div>
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          All banners
        </h2>
        {banners === null ? (
          <p className="text-slate-500 text-sm">Loading…</p>
        ) : banners.length === 0 ? (
          <p className="text-slate-500 text-sm">No banners yet.</p>
        ) : (
          <ul className="space-y-2">
            {banners.map((b) => (
              <li
                key={b.id}
                className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span
                    className="inline-block h-4 w-4 rounded shrink-0"
                    style={{ background: b.bg_color }}
                  />
                  <span className="truncate text-sm text-slate-800">
                    {b.message}
                  </span>
                  {b.enabled ? (
                    <span className="rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5">
                      LIVE
                    </span>
                  ) : (
                    <span className="rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold px-2 py-0.5">
                      OFF
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-3 shrink-0">
                  <button
                    type="button"
                    onClick={() => edit(b)}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(b.id)}
                    className="text-xs font-semibold text-rose-600 hover:text-rose-800"
                  >
                    Delete
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
